import React, { useEffect, useMemo, useState } from "react";

import { BrokerCsvFileUploadField } from "@/components/Broker/BrokerCsvFileUploadField";
import {
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
  BROKER_PILL_BADGE_CLASS,
  BROKER_PILL_BADGE_LABEL_STYLE,
} from "@/components/Broker/brokerAlertUi";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { CustomCsvImportCapabilities } from "../../../services/brokerIntegrationApi";
import { postCustomCsvInspect } from "../../../services/customCsvImportApi";
import type {
  CustomCsvInspectPreviewRow,
  CustomCsvWizardState,
} from "../../../types/customCsvImport";
import {
  csvImportMaxSizeMessage,
  isCsvImportFileWithinLimit,
} from "../../../constants/securityLimits";
import {
  mappingHeadersFromInspect,
  sanitizeCsvCellDisplay,
} from "../../../utils/customCsvImport";

export interface CustomCsvUploadStepProps {
  csvImport: CustomCsvImportCapabilities;
  state: CustomCsvWizardState;
  onStateChange: (patch: Partial<CustomCsvWizardState>) => void;
  detectedHeaders: string[];
  onDetectedHeadersChange: (headers: string[]) => void;
}

const HEADER_PREVIEW_STICKY_HEAD_CLASS =
  "sticky left-0 z-40 min-w-[5.5rem] bg-background shadow-[1px_0_0_0_hsl(var(--border))]";

const HEADER_PREVIEW_STICKY_CELL_CLASS =
  "sticky left-0 z-10 min-w-[5.5rem] bg-inherit shadow-[1px_0_0_0_hsl(var(--border))] align-middle";

const HEADER_PREVIEW_TABLE_SHELL_CLASS =
  "w-full overflow-hidden rounded-[6px] border border-border bg-background";

function displayCellValue(cell: string | undefined): string {
  const trimmed = String(cell ?? "").trim();
  if (!trimmed) return "—";
  return sanitizeCsvCellDisplay(trimmed);
}

const CustomCsvUploadStep = ({
  csvImport,
  state,
  onStateChange,
  onDetectedHeadersChange,
}: CustomCsvUploadStepProps): React.ReactElement => {
  const [error, setError] = useState<string | null>(null);
  const [inspectLoading, setInspectLoading] = useState(false);

  // Single source of truth: chosen inspect row (or suggested_headers) — never merge other rows.
  const mappingHeaders = useMemo(
    () => mappingHeadersFromInspect(state.inspectResult, state.headerRowIndex),
    [state.inspectResult, state.headerRowIndex]
  );

  useEffect(() => {
    onDetectedHeadersChange(mappingHeaders);
  }, [mappingHeaders, onDetectedHeadersChange]);

  useEffect(() => {
    if (!state.csvFile) {
      onStateChange({
        headerRowIndex: 0,
        inspectResult: null,
      });
      return;
    }

    let cancelled = false;
    setInspectLoading(true);
    setError(null);

    void postCustomCsvInspect(state.csvFile, {
      endpoint: csvImport.inspect_endpoint,
      maxPreviewRows: 15,
    })
      .then((inspect) => {
        if (cancelled) return;
        const suggested = Math.max(0, Math.floor(inspect.suggested_header_row_index ?? 0));
        onStateChange({
          headerRowIndex: suggested,
          inspectResult: inspect,
          preview: null,
          acknowledgedWarningCodes: new Set(),
          columnMapping: {},
        });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        onStateChange({
          headerRowIndex: 0,
          inspectResult: null,
          preview: null,
          acknowledgedWarningCodes: new Set(),
        });
        setError(e instanceof Error ? e.message : "Could not inspect this CSV.");
      })
      .finally(() => {
        if (!cancelled) setInspectLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [state.csvFile, csvImport.inspect_endpoint]);

  const onFileChange = (file: File | null) => {
    setError(null);
    if (!file) {
      onStateChange({
        csvFile: null,
        headerRowIndex: 0,
        inspectResult: null,
        preview: null,
        acknowledgedWarningCodes: new Set(),
        columnMapping: {},
      });
      return;
    }
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setError("Expected a .csv file.");
      return;
    }
    if (!isCsvImportFileWithinLimit(file)) {
      setError(csvImportMaxSizeMessage());
      return;
    }
    onStateChange({
      csvFile: file,
      headerRowIndex: 0,
      inspectResult: null,
      preview: null,
      acknowledgedWarningCodes: new Set(),
      columnMapping: {},
    });
  };

  const selectHeaderRow = (row: CustomCsvInspectPreviewRow) => {
    onStateChange({
      headerRowIndex: row.row_index,
      preview: null,
      acknowledgedWarningCodes: new Set(),
      columnMapping: {},
    });
  };

  const previewRows = state.inspectResult?.preview_rows ?? [];
  const previewColumnCount = useMemo(
    () =>
      previewRows.reduce((max, row) => Math.max(max, row.cells?.length ?? 0), 0),
    [previewRows]
  );

  return (
    <div className="flex flex-col gap-4">
      <BrokerCsvFileUploadField
        id="custom-csv-upload-file"
        label="CSV File"
        description="Upload your broker export (.csv, max 5 MB). We will use it for mapping and preview."
        accept=".csv,text/csv"
        file={state.csvFile}
        onFileChange={onFileChange}
        required
      />
      {error ? (
        <p className="m-0 font-[family-name:var(--font-primary)] text-sm text-destructive">
          {error}
        </p>
      ) : null}
      {inspectLoading ? (
        <p className="m-0 font-[family-name:var(--font-primary)] text-sm text-muted-foreground">
          Scanning file for column titles…
        </p>
      ) : null}
      {previewRows.length > 0 ? (
        <Field className="gap-2">
          <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Which row has column titles?</FieldLabel>
          <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
            Some exports put a date or banner above the real headers. Pick the row with column
            names (Suggested is pre-selected).
          </FieldDescription>
          <div
            data-custom-csv-header-preview-scroll=""
            className={cn(HEADER_PREVIEW_TABLE_SHELL_CLASS, "min-h-0 max-h-64")}
          >
            <div
              data-shadcn-data-table
              data-shadcn-data-table-borderless
              data-custom-csv-header-preview-table=""
            >
              <Table>
                <TableHeader className="sticky top-0 z-30 bg-background shadow-[0_1px_0_0_hsl(var(--border))]">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className={HEADER_PREVIEW_STICKY_HEAD_CLASS}>Row</TableHead>
                    {Array.from({ length: previewColumnCount }, (_, columnIndex) => (
                      <TableHead key={columnIndex} className="min-w-[6rem]">
                        Col {columnIndex + 1}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
              <TableBody>
                {previewRows.map((row) => {
                  const selected = row.row_index === state.headerRowIndex;
                  const cells = row.cells ?? [];
                  return (
                    <TableRow
                      key={row.row_index}
                      role="button"
                      tabIndex={0}
                      data-state={selected ? "selected" : undefined}
                      aria-selected={selected}
                      onClick={() => selectHeaderRow(row)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          selectHeaderRow(row);
                        }
                      }}
                      className="cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--brand-tropical-surf)]/40"
                    >
                      <TableCell className={HEADER_PREVIEW_STICKY_CELL_CLASS}>
                        <div className="flex flex-col gap-0.5">
                          <span>{row.row_index}</span>
                          {row.is_suggested_header ? (
                            <span
                              data-broker-pill-badge=""
                              className={cn(BROKER_PILL_BADGE_CLASS, "self-start")}
                            >
                              <span style={BROKER_PILL_BADGE_LABEL_STYLE}>Suggested</span>
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      {Array.from({ length: previewColumnCount }, (_, columnIndex) => (
                        <TableCell
                          key={columnIndex}
                          title={displayCellValue(cells[columnIndex])}
                        >
                          {displayCellValue(cells[columnIndex])}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
                </TableBody>
              </Table>
            </div>
          </div>
        </Field>
      ) : null}
      {mappingHeaders.length > 0 ? (
        <Field className="gap-2">
          <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Columns for mapping</FieldLabel>
          <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
            From header row {state.headerRowIndex}.
          </FieldDescription>
          <div className="flex flex-wrap gap-2">
            {mappingHeaders.map((header, index) => (
              <span
                key={`${index}:${header}`}
                data-broker-pill-badge=""
                className={BROKER_PILL_BADGE_CLASS}
              >
                <span style={BROKER_PILL_BADGE_LABEL_STYLE}>{header}</span>
              </span>
            ))}
          </div>
        </Field>
      ) : null}
    </div>
  );
};

export default CustomCsvUploadStep;
